import { A } from '@solidjs/router';
import { Show, createSignal } from 'solid-js';
import { t } from '@/shared/i18n';
import { usePublicPath } from '@/shared/lib/usePublicPath';
import { LanguageSwitcher } from '@/shared/components/LanguageSwitcher';
import { isAuthenticated } from '@/features/auth/store/session.store';

interface MarketingNavbarProps {
  lightTheme?: boolean;
}

export default function MarketingNavbar(props: MarketingNavbarProps = {}) {
  const [mobileOpen, setMobileOpen] = createSignal(false);
  const lightTheme = () => props.lightTheme ?? false;
  const pp = usePublicPath();

  return (
    <nav
      class={`sticky top-0 z-30 border-b backdrop-blur-lg ${
        lightTheme()
          ? 'border-slate-200/90 bg-white/95'
          : 'border-white/20 bg-amber-700/85'
      }`}
    >
      <div class="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        <A
          href={pp('/welcome')}
          class={`text-2xl font-extrabold tracking-tight ${lightTheme() ? 'text-amber-700' : 'text-white'}`}
        >
          khoai.io
        </A>

        <div class="hidden items-center gap-5 lg:flex">
          <A
            href={pp('/pricing')}
            class={`text-sm font-semibold ${lightTheme() ? 'text-slate-700 hover:text-slate-900' : 'text-white/85 hover:text-white'}`}
          >
            {t('public.nav.pricing')}
          </A>
          <A
            href={pp('/blogs')}
            class={`text-sm font-semibold ${lightTheme() ? 'text-slate-700 hover:text-slate-900' : 'text-white/85 hover:text-white'}`}
          >
            Blog
          </A>
          <Show
            when={isAuthenticated()}
            fallback={
              <A
                href="/login"
                class={`text-sm font-semibold ${lightTheme() ? 'text-slate-700 hover:text-slate-900' : 'text-white/85 hover:text-white'}`}
              >
                {t('public.nav.login')}
              </A>
            }
          >
            <A
              href="/"
              class={`text-sm font-semibold ${lightTheme() ? 'text-slate-700 hover:text-slate-900' : 'text-white/85 hover:text-white'}`}
            >
              Dashboard
            </A>
          </Show>
          <LanguageSwitcher onDark={!lightTheme()} />
          <Show
            when={!isAuthenticated()}
            fallback={
              <A
                href="/"
                class={`rounded-xl px-4 py-2.5 text-sm font-bold transition ${
                  lightTheme()
                    ? 'bg-amber-600 text-white shadow-[0_10px_24px_rgba(180,83,9,0.2)] hover:bg-amber-700'
                    : 'bg-white text-amber-800 shadow-[0_10px_24px_rgba(120,53,15,0.25)] hover:bg-amber-50'
                }`}
              >
                Open App
              </A>
            }
          >
            <A
              href="/signup"
              class={`rounded-xl px-4 py-2.5 text-sm font-bold transition ${
                lightTheme()
                  ? 'bg-amber-600 text-white shadow-[0_10px_24px_rgba(180,83,9,0.2)] hover:bg-amber-700'
                  : 'bg-white text-amber-800 shadow-[0_10px_24px_rgba(120,53,15,0.25)] hover:bg-amber-50'
              }`}
            >
              {t('public.nav.signup')}
            </A>
          </Show>
        </div>

        <button
          class={`rounded-lg p-2 lg:hidden ${
            lightTheme()
              ? 'text-slate-700 hover:bg-slate-100'
              : 'text-white hover:bg-white/10'
          }`}
          onClick={() => setMobileOpen(!mobileOpen())}
          aria-label="Toggle menu"
        >
          <Show
            when={mobileOpen()}
            fallback={
              <svg
                class="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            }
          >
            <svg
              class="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </Show>
        </button>
      </div>

      <Show when={mobileOpen()}>
        <div
          class={`border-t px-4 pb-4 pt-2 lg:hidden ${
            lightTheme() ? 'border-slate-200' : 'border-white/20'
          }`}
        >
          <div class="flex flex-col gap-1">
            <A
              href={pp('/pricing')}
              class={`rounded-lg px-3 py-2 text-sm font-semibold ${
                lightTheme()
                  ? 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                  : 'text-white/85 hover:bg-white/10 hover:text-white'
              }`}
              onClick={() => setMobileOpen(false)}
            >
              {t('public.nav.pricing')}
            </A>
            <A
              href={pp('/blogs')}
              class={`rounded-lg px-3 py-2 text-sm font-semibold ${
                lightTheme()
                  ? 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                  : 'text-white/85 hover:bg-white/10 hover:text-white'
              }`}
              onClick={() => setMobileOpen(false)}
            >
              Blog
            </A>
            <Show
              when={isAuthenticated()}
              fallback={
                <>
                  <A
                    href="/login"
                    class={`rounded-lg px-3 py-2 text-sm font-semibold ${
                      lightTheme()
                        ? 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                        : 'text-white/85 hover:bg-white/10 hover:text-white'
                    }`}
                    onClick={() => setMobileOpen(false)}
                  >
                    {t('public.nav.login')}
                  </A>
                  <A
                    href="/signup"
                    class={`mt-1 rounded-xl px-3 py-2 text-center text-sm font-bold ${
                      lightTheme()
                        ? 'bg-amber-600 text-white hover:bg-amber-700'
                        : 'bg-white text-amber-800 hover:bg-amber-50'
                    }`}
                    onClick={() => setMobileOpen(false)}
                  >
                    {t('public.nav.signup')}
                  </A>
                </>
              }
            >
              <A
                href="/"
                class={`rounded-lg px-3 py-2 text-sm font-semibold ${
                  lightTheme()
                    ? 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                    : 'text-white/85 hover:bg-white/10 hover:text-white'
                }`}
                onClick={() => setMobileOpen(false)}
              >
                Dashboard
              </A>
              <A
                href="/"
                class={`mt-1 rounded-xl px-3 py-2 text-center text-sm font-bold ${
                  lightTheme()
                    ? 'bg-amber-600 text-white hover:bg-amber-700'
                    : 'bg-white text-amber-800 hover:bg-amber-50'
                }`}
                onClick={() => setMobileOpen(false)}
              >
                Open App
              </A>
            </Show>
            <div
              class={`mt-2 border-t pt-2 ${
                lightTheme() ? 'border-slate-200' : 'border-white/20'
              }`}
            >
              <LanguageSwitcher onDark={!lightTheme()} />
            </div>
          </div>
        </div>
      </Show>
    </nav>
  );
}
